import React, { useEffect, useRef, useState } from 'react';
// Primero, importa animate al inicio del archivo junto con los otros imports
import { motion, useMotionValue, useTransform, animate, useMotionValueEvent } from 'framer-motion';
import { getPredicciones, Prediccion } from '../../../services/hipodromo';
import {useParams} from "react-router-dom";
import Chip from "@mui/material/Chip";
import {Box, Button, Card, CardContent, Typography} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {getColors} from '../../../componetes/color_racers'

// Dentro del componente Simulacion, antes del return, agrega:
const Simulacion: React.FC = () => {
    const { id } = useParams();
    const [startRace, setStartRace] = useState(false);
    const [trackWidth, setTrackWidth] = useState(0);
    const [predicciones, setPredicciones] = useState<Prediccion[]>([]);
    const [error, setError] = useState<string>('');
    const trackRef = useRef<HTMLDivElement>(null);

    // El componente AnimatedChip corregido:
const AnimatedChip: React.FC<{
    value: number;
    color: string;
    textColor: string;
    startRace: boolean;
    delay: number;
}> = ({ value, color, textColor, startRace, delay }) => {
    const progress = useMotionValue(0);
    const displayValue = useTransform(progress, latest => (latest * value * 100).toFixed(0));
    const [display, setDisplay] = useState<string>("0");

    useMotionValueEvent(displayValue, "change", (latest: string) => {
        setDisplay(latest);
    });

    React.useEffect(() => {
        if (startRace) {
            const controls = animate(progress, 1, {
                duration: 2,
                delay: delay / 1000,
                ease: "easeOut"
            });
            
            return controls.stop;
        } else {
            progress.set(0);
        }
    }, [startRace, delay, progress]);

    return (
        <Chip 
            label={` 🐎 ${display}%`}
            sx={{
                backgroundColor: color,
                color: textColor,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                whiteSpace: 'nowrap'
            }}
            size="small"
        />
    );
};

    useEffect(() => {
        const fetchPredicciones = async () => {
            try {
                const data = await getPredicciones(Number(id));
                setPredicciones(data);
                 const updateWidth = () => {
                    if (trackRef.current) {
                        setTrackWidth(trackRef.current.offsetWidth);
                    }
                };
                // Actualizar el ancho inicial
                updateWidth();
                // Configurar un observer para detectar cambios en el tamaño
                const resizeObserver = new ResizeObserver(updateWidth);
                if (trackRef.current) {
                    resizeObserver.observe(trackRef.current);
                }

                // También mantener el listener de resize para compatibilidad
                window.addEventListener('resize', updateWidth);

                return () => {
                    resizeObserver.disconnect();
                    window.removeEventListener('resize', updateWidth);
                };
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Error al cargar predicciones');
            }
        };

        fetchPredicciones();


    }, [id]);



    if (error) {
        return (
            <Box sx={{ width: '100%', p: 2, bgcolor: '#a0522d', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Card sx={{ p: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Card>
            </Box>
        );
    }

    if (predicciones.length === 0) {
        return (
            <Box sx={{ width: '100%', p: 2, bgcolor: '#a0522d', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </Box>
        );
    }

    const maxPrediction = Math.max(...predicciones.map(p => parseFloat(p.pro_car_predition)));

    const handleStartRace = () => {
        // setReset(false);
        setStartRace(true);
    };

    const handleReset = () => {
        setStartRace(false);
        setTimeout(() => {

        }, 50);
    };

    // En el map de predicciones, reemplaza el Chip existente con esto:
    return (
        <Card sx={{ 
            marginTop: { xs: 1, sm: 1 },
            mx: { xs: 1, sm: 1 },
            overflow: 'hidden',
            maxWidth: '100%',
            margin: 10,
        }}>
            <Box sx={{ p: 2 }}>
                <Typography textAlign="center" variant="h5" component="div" gutterBottom>
                    Simulación de carreras
                </Typography>
                
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleStartRace}
                        disabled={startRace}
                        startIcon={<PlayArrowIcon />}
                        size="large"
                        sx={{ minWidth: '32px' }}
                    >

                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        startIcon={<RestartAltIcon />}
                        size="large"
                        sx={{ minWidth: '32px' }}
                    >

                    </Button>
                </Box>
            </Box>

            <CardContent sx={{ 
                bgcolor: '#a0522d',
                p: { xs: 0.5, sm: 2 }, // Reduce el padding en móviles
                minHeight: '70vh',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <Box
                    ref={trackRef}
                    sx={{ 
                        position: 'relative',
                        width: '100%',
                        minHeight: '60vh',
                        p: { xs: '0.5rem', sm: '1rem' },
                        backgroundColor: '#a0522d',
                        overflow: 'hidden',
                        margin: '0 auto',
                        maxWidth: '100%'
                    }}
                >
                    {/* Línea de meta */}
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: `${Math.min(trackWidth * 0.60, trackWidth - 50)}px`, // Reducido a 60%
                            width: '2px',
                            backgroundColor: 'white',
                            opacity: 0.8
                        }}
                    />

                    {predicciones.map((prediccion, index) => {
                        const value = parseFloat(prediccion.pro_car_predition);
                        const availableWidth = trackWidth * (window.innerWidth < 670 ? 0.35 : 0.75); // Ajusta el espacio disponible para pantallas pequeñas
                        const normalizedPixels = (value / maxPrediction) * availableWidth;
                        const { color, textColor} = getColors(prediccion.pro_car_inscripcion)

                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    borderBottom: '1px solid #7a3e1d',
                                    py: 1,
                                    height: '3rem',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    width: '100%', // Aseguramos ancho completo
                                    '&:hover': {
                                        bgcolor: '#8b4513'
                                    }
                                }}
                            >
                                <Typography
                                    sx={{
                                        minWidth: '50px',
                                        textAlign: 'left',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        mr: 1,
                                        flexShrink: 1 // Evita que el texto se encoja
                                    }}
                                >
                                    #{prediccion.pro_car_inscripcion} {prediccion.pro_car_eje_name}
                                </Typography>
                                <Box sx={{ 
                                    position: 'relative',
                                    overflow: 'hidden',
                                    flexGrow: 1,
                                    width: '100%'
                                }}>
                                    <motion.div
                                        initial={{ x: 0 }}
                                        animate={startRace ? { x: normalizedPixels } : { x: 0 }}
                                        transition={{ 
                                            duration: 2,
                                            ease: 'easeOut',
                                            delay: index * 0.1
                                        }}
                                    >
                                        <AnimatedChip
                                            value={value}
                                            color={color}
                                            textColor={textColor}
                                            startRace={startRace}
                                            delay={index * 100}
                                        />
                                    </motion.div>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
};

export default Simulacion;